import { itemName } from "../world/items";
import { drawItemIcon } from "./icons";
import { sound } from "../audio/sound-engine";

export interface PickerItem {
  id: number;
  /** Owned count to display, or null to hide it (the creative palette). */
  count: number | null;
}

/**
 * The creative block palette, opened with E in creative mode. Click any block
 * to drop it into the selected hotbar slot. Survival uses the inventory screen
 * instead.
 */
export class Picker {
  open = false;

  /** Called with the chosen block id. */
  onPick: ((id: number) => void) | null = null;
  /** Called when the player asks to close without picking (button click). */
  onClose: (() => void) | null = null;

  private readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly gridEl: HTMLElement;
  private readonly emptyEl: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = must(root.querySelector<HTMLElement>("#picker"));
    this.titleEl = must(root.querySelector<HTMLElement>("#picker-title"));
    this.gridEl = must(root.querySelector<HTMLElement>("#picker-grid"));
    this.emptyEl = must(root.querySelector<HTMLElement>("#picker-empty"));

    const close = must(root.querySelector<HTMLButtonElement>("#picker-close"));
    close.onclick = () => {
      sound.playUiClick();
      this.onClose?.();
    };
  }

  show(title: string, items: PickerItem[]): void {
    this.titleEl.textContent = title;
    this.gridEl.textContent = "";
    this.emptyEl.style.display = items.length === 0 ? "block" : "none";

    for (const item of items) {
      const slot = document.createElement("button");
      slot.type = "button";
      slot.className = "picker-slot";
      slot.title = itemName(item.id);

      const icon = document.createElement("canvas");
      icon.width = 32;
      icon.height = 32;
      icon.className = "slot-icon";
      drawItemIcon(icon, item.id);
      slot.appendChild(icon);

      const label = document.createElement("span");
      label.className = "picker-name";
      label.textContent = itemName(item.id);
      slot.appendChild(label);

      if (item.count !== null) {
        const count = document.createElement("span");
        count.className = "picker-count";
        count.textContent = String(item.count);
        slot.appendChild(count);
      }

      slot.addEventListener("click", () => {
        sound.playUiClick();
        this.onPick?.(item.id);
      });
      this.gridEl.appendChild(slot);
    }

    this.root.hidden = false;
    this.open = true;
  }

  hide(): void {
    this.root.hidden = true;
    this.open = false;
  }
}

function must<T extends Element>(el: T | null): T {
  if (!el) throw new Error("HUD element missing from index.html");
  return el;
}
